import { Button, Grid, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField } from "@mui/material";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Helpers } from "../../../utils/helpers";
import { fetchTournamentList, TournamentEnvironmentListItem, TournamentListResponse } from "../api";
import { renderTournamentStatus, renderTournamentValue, TournamentEmptyState, TournamentPageHeader, TournamentSourceNotice } from "../components";

const initialFilters = {
    query: "",
    type: "",
    status: "",
    page: 1,
    pageSize: 10,
};

export const TournamentEnvironments = (props: any) => {
    const [filters, setFilters] = React.useState(initialFilters);
    const [response, setResponse] = React.useState<TournamentListResponse<TournamentEnvironmentListItem> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Tournament Environments");
        loadList(initialFilters);
    }, []);

    const loadList = (nextFilters = filters) => {
        setLoading(true);
        setError(null);

        fetchTournamentList<TournamentEnvironmentListItem>("/api/tournaments/environments", nextFilters, props.activeEnv)
            .then(data => {
                setResponse(data);
                setFilters(nextFilters);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load tournament environments.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (key: string) => (event) => {
        setFilters({
            ...filters,
            [key]: event.target.value,
        });
    };

    const handleSearch = () => loadList({ ...filters, page: 1 });
    const handleClear = () => loadList(initialFilters);

    const handlePageChange = (_event: unknown, page: number) => {
        loadList({
            ...filters,
            page: page + 1,
        });
    };

    const handleRowsPerPageChange = (event) => {
        loadList({
            ...filters,
            page: 1,
            pageSize: parseInt(event.target.value, 10),
        });
    };

    const items = response?.data.items || [];
    const page = (response?.data.page || 1) - 1;
    const pageSize = response?.data.pageSize || filters.pageSize;
    const total = response?.data.total || 0;

    return <div className="contentPadding">
        <TournamentPageHeader
            title="Tournament / Environment"
        />

        <Paper className="root" elevation={0} style={{ marginTop: 16 }}>
            <div style={{ float: "left", width: "100%", padding: "22px 22px 0 22px" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            label="Search"
                            value={filters.query}
                            onChange={handleFilterChange("query")}
                            placeholder="Environment ID, name, type, version"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Type"
                            value={filters.type}
                            onChange={handleFilterChange("type")}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="lava">Lava</MenuItem>
                            <MenuItem value="jungle">Jungle</MenuItem>
                            <MenuItem value="desert">Desert</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            value={filters.status}
                            onChange={handleFilterChange("status")}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="review">Review</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>

                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <Button disableElevation variant="contained" color="primary" onClick={handleSearch}>
                        Search
                    </Button>
                    <Button disableElevation variant="contained" color="secondary" onClick={handleClear} style={{ marginLeft: 8 }}>
                        Clear
                    </Button>
                </div>
            </div>

            <div style={{ float: "left", width: "100%", padding: "0 22px 22px 22px" }}>
                <TournamentSourceNotice meta={response?.meta} />

                <LoadingWrapper loading={loading} error={error}>
                    {items.length === 0 ? <TournamentEmptyState
                        title="No environments found"
                        description="Try clearing the filters or connect the adapter to the authoritative environment source."
                    /> : <React.Fragment>
                        <Table className="min-table-size">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Environment ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Linked layouts</TableCell>
                                    <TableCell>Battle usage</TableCell>
                                    <TableCell>Updated at</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map(item => <TableRow hover key={item.id}>
                                    <TableCell>
                                        <Button size="small" color="primary" onClick={() => props.history.push(`/tournament/environments/${item.id}`)}>
                                            {item.id}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{renderTournamentValue(item.name)}</TableCell>
                                    <TableCell>{renderTournamentValue(item.type)}</TableCell>
                                    <TableCell>{renderTournamentValue(item.version)}</TableCell>
                                    <TableCell>{renderTournamentStatus(item.status)}</TableCell>
                                    <TableCell>{item.linkedLayoutsCount}</TableCell>
                                    <TableCell>{item.battleUsageCount}</TableCell>
                                    <TableCell>{item.updatedAt ? Helpers.formatDateWithTime(item.updatedAt, true) : "n/a"}</TableCell>
                                    <TableCell>
                                        <Button size="small" variant="outlined" onClick={() => props.history.push(`/tournament/environments/${item.id}`)}>
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 20]}
                        />
                    </React.Fragment>}
                </LoadingWrapper>
            </div>
        </Paper>
    </div>;
};
